import type { AxiosHeaders } from "axios";

/**
 * Marks a request as an API call for Novi's edge, which otherwise decides by
 * Sec-Fetch-Dest and treats a request it reads as page navigation differently:
 * a 302 to login instead of a 401, and for a user with several customer
 * affiliations the HTML customer selector as a 200. A tile request once got
 * that page and rendered as a broken layer with no error. X-Requested-With is
 * the signal the edge checks first and the one every client here can set,
 * including mapbox's tile worker.
 *
 * Only same-origin requests carry it: those are the only ones the edge sees,
 * since a customer host configures its service roots as relative paths, and a
 * cross-origin request would need the header on the service's CORS allow list.
 */
const REQUESTED_WITH = "X-Requested-With";

export const XML_HTTP_REQUEST = "XMLHttpRequest";

export const API_REQUEST_HEADERS: Readonly<Record<string, string>> = {
  [REQUESTED_WITH]: XML_HTTP_REQUEST
};

/**
 * Whether `url`, resolved against `base` (axios's baseURL) or the page, shares
 * the page's origin. A relative url is same-origin; an unparsable one is not.
 */
export const isSameOrigin = (url: Maybe<string>, base?: string): boolean => {
  try {
    return (
      new URL(url ?? "", base ?? window.location.href).origin === window.location.origin
    );
  } catch {
    return false;
  }
};

/** The marker for a request to `url`, or nothing when it would cross origins. */
export const apiRequestHeadersFor = (
  url: Maybe<string>
): Readonly<Record<string, string>> => (isSameOrigin(url) ? API_REQUEST_HEADERS : {});

/**
 * Adds the marker to an outgoing axios request bound for the page's origin,
 * keeping a value the caller set.
 */
export const markAsApiRequest = (
  headers: AxiosHeaders,
  url: Maybe<string>,
  base?: string
): AxiosHeaders =>
  isSameOrigin(url, base)
    ? headers.set(REQUESTED_WITH, XML_HTTP_REQUEST, false)
    : headers;
